/**
 * Показ раздела «Смена» без сервера: `VITE_MOCK_API=1`.
 *
 * Выдуманная смена в форме ответов `portal_shift.py`: чек-листы по участкам
 * с ответами, задачи на доске, табель (табель прихода, таблица, цеха), два шаблона и дисциплина. Действия
 * (взять и закрыть задачу, приход и уход, правка шаблона) живут в памяти
 * вкладки. Правил сервиса здесь нет — только формы ответов.
 */

import { ApiError } from '@shell/api';
import type { PortalRequest } from '../../api';
import type {
  ChecklistDay,
  RunDetail,
  RunRow,
  TaskCard,
  TaskRow,
  TemplateDetail,
  TemplateItem,
  BreakfastMode,
  DaySheet,
  GridRow,
  HoursGrid,
  PositionSetup,
  PositionsSetup,
  SheetEntry,
} from './api';

const now = Date.now();
const iso = (offsetMinutes: number) => new Date(now + offsetMinutes * 60_000).toISOString();
const day = (offset: number) => new Date(now + offset * 86_400_000).toISOString().slice(0, 10);

function run(
  id: number,
  title: string,
  area: [number, string] | null,
  position: string,
  from: number,
  to: number,
  status: string,
  performer: string | null,
  answered: number,
  total: number,
): RunRow {
  const titles: Record<string, string> = {
    pending: 'не начат',
    in_progress: 'в работе',
    done: 'выполнен',
    done_late: 'с опозданием',
    missed: 'просрочен',
  };
  return {
    id,
    template_id: id,
    title,
    date: day(0),
    area_id: area?.[0] ?? null,
    area: area?.[1] ?? null,
    position,
    window_from: iso(from),
    window_to: iso(to),
    status,
    status_title: titles[status],
    performer,
    answered,
    total,
    started_at: performer ? iso(from + 10) : null,
    finished_at: status === 'done' || status === 'done_late' ? iso(from + 40) : null,
  };
}

const KITCHEN: [number, string] = [1, 'Кухня'];
const BAR: [number, string] = [2, 'Бар'];
const HALL: [number, string] = [3, 'Зал'];

const runs: RunRow[] = [
  run(501, 'Открытие кухни', KITCHEN, 'Повар', -240, -120, 'done', 'Анна Морозова', 6, 6),
  run(502, 'Температуры холодильников', KITCHEN, 'Повар', -60, 60, 'in_progress', 'Иван Петров', 2, 4),
  run(503, 'Открытие бара', BAR, 'Бармен', -200, -80, 'done_late', 'Олег Смирнов', 5, 5),
  run(504, 'Подготовка зала', HALL, 'Официант', -180, -90, 'missed', null, 0, 7),
  run(505, 'Закрытие кухни', KITCHEN, 'Повар', 600, 720, 'pending', null, 0, 5),
];

function checklistDay(date: string | null): ChecklistDay {
  const target = date ?? day(0);
  const list = target === day(0) ? runs : [];
  const groups = new Map<string, { area_id: number | null; title: string; runs: RunRow[] }>();
  for (const row of list) {
    const key = row.area ?? 'Без участка';
    const group = groups.get(key) ?? { area_id: row.area_id, title: key, runs: [] };
    group.runs.push(row);
    groups.set(key, group);
  }
  const count = (status: string) => list.filter((row) => row.status === status).length;
  return {
    date: target,
    today: day(0),
    whole_shift: true,
    totals: {
      planned: list.length,
      done: count('done'),
      done_late: count('done_late'),
      missed: count('missed'),
      open: count('pending') + count('in_progress'),
    },
    areas: [...groups.values()],
  };
}

function runDetail(id: number): RunDetail {
  const row = runs.find((item) => item.id === id);
  if (!row) throw new ApiError(404, 'not found', 'Чек-лист не найден.');
  const answered = row.answered;
  const plan = [
    { title: 'Холодильная камера №1 закрыта', type: 'bool', value: 'Да' },
    { title: 'Температура камеры №2', type: 'number', value: '6 °C', allowed: 'от -2 до 4 °C', problem: 'Значение 6 °C вне допустимого (от -2 до 4 °C).' },
    { title: 'Срок годности соусов проверен', type: 'date', value: '18.09.2026' },
    { title: 'Фото рабочего места', type: 'photo', value: null },
    { title: 'Вытяжка работает', type: 'bool', value: null, cant: 'Не включается, вызвали техника' },
    { title: 'Уборка стола раздачи', type: 'bool', value: 'Да' },
  ].slice(0, row.total);
  return {
    run: row,
    note: 'Чек-листы заполняют в операционном боте. Здесь — только просмотр.',
    items: plan.map((item, index) => {
      const done = index < answered;
      const cant = done && item.cant;
      return {
        id: id * 10 + index,
        order_no: index + 1,
        title: item.title,
        hint: null,
        field_type: item.type,
        is_critical: index === 1,
        photo_required: item.type === 'photo',
        allowed: item.allowed ?? null,
        state: done ? (cant ? 'cant' : 'done') : null,
        state_title: done ? (cant ? 'не смог' : 'сделано') : null,
        value: done && !cant ? item.value : null,
        comment: cant ? item.cant ?? null : null,
        answered_at: done ? iso(-200 + index * 3) : null,
        problem: done ? (cant ? item.cant ?? null : item.problem ?? null) : null,
        photos:
          done && item.type === 'photo'
            ? [
                {
                  photo_id: 900 + index,
                  url: PHOTO,
                  available: true,
                  received_at: iso(-190),
                  forwarded: false,
                  width: 1,
                  height: 1,
                },
              ]
            : [],
        tasks:
          done && item.problem
            ? [{ id: 701, title: item.title, status: 'open', status_title: 'новая' }]
            : [],
      };
    }),
  };
}

const PHOTO =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96"><rect width="96" height="96" fill="#e7ded3"/><text x="48" y="54" font-size="12" text-anchor="middle" fill="#7a6a5a">фото</text></svg>',
  );

const STATUS_TITLES: Record<string, string> = {
  open: 'новая',
  in_progress: 'в работе',
  resolved: 'закрыта',
  cancelled: 'отменена',
};

function task(id: number, title: string, extra: Partial<TaskRow> = {}): TaskRow {
  return {
    id,
    title,
    description: 'Замечено при прохождении чек-листа.',
    status: 'open',
    status_title: 'новая',
    active: true,
    area: 'Кухня',
    position: 'Техник',
    assignee: null,
    due_at: iso(300),
    overdue: false,
    photo_required: false,
    from_checklist: true,
    created_at: iso(-180),
    created_by: 'Иван Петров',
    resolved_at: null,
    resolved_by: null,
    comment: null,
    ...extra,
  };
}

const tasks: TaskRow[] = [
  task(701, 'Температура камеры №2', { description: 'Значение 6 °C вне допустимого (от -2 до 4 °C).', due_at: iso(-20), overdue: true, position: 'Шеф-повар' }),
  task(702, 'Не включается вытяжка', { photo_required: true, description: 'Не включается, вызвали техника' }),
  task(703, 'Протекает кран в баре', { area: 'Бар', status: 'in_progress', status_title: 'в работе', assignee: 'Олег Смирнов', from_checklist: false, created_by: 'Олег Смирнов' }),
  task(704, 'Заменить лампу в зале', { area: 'Зал', status: 'resolved', status_title: 'закрыта', active: false, assignee: 'Сергей Техников', resolved_by: 'Сергей Техников', resolved_at: iso(-600), comment: 'Лампа заменена', from_checklist: false }),
];

/** Снимки, приложенные к задачам в этой вкладке, — показ без сервера. */
const photoByTask = new Map<number, TaskCard['photos']['resolution'][number]>();

function taskCard(row: TaskRow): TaskCard {
  const history: TaskCard['history'] = [{ at: row.created_at, text: `Заведена: ${row.created_by}` }];
  if (row.assignee) history.push({ at: null, text: `Взял в работу: ${row.assignee}` });
  if (row.status === 'resolved') history.push({ at: row.resolved_at, text: `Закрыта: ${row.resolved_by}` });
  if (row.status === 'cancelled') history.push({ at: row.resolved_at, text: 'Отменена управляющим' });
  const actions: TaskCard['actions'] = [];
  if (row.active) {
    if (!row.assignee) actions.push('take');
    actions.push('resolve', 'cancel');
  }
  return {
    task: row,
    source: row.from_checklist
      ? { run_id: 502, checklist: 'Температуры холодильников', date: day(0), item: row.title, may_open: true }
      : null,
    history,
    photos: {
      problem: [],
      resolution: [photoByTask.get(row.id)].filter(Boolean) as TaskCard['photos']['resolution'],
    },
    actions,
  };
}

/** Табель (D214): цеха листа, люди и отметки — формы ответов, без правил. */
const sheetPositions: PositionSetup[] = [
  { position_id: 31, name: 'Менеджер', section: 'Менеджер', section_order: 10, breakfast: 'none' , kitchen_arrival: false, department: 'Зал' },
  { position_id: 32, name: 'Бармен', section: 'Бармен', section_order: 40, breakfast: 'none' , kitchen_arrival: false, department: 'Зал' },
  { position_id: 33, name: 'Официант', section: 'Официанты', section_order: 50, breakfast: 'hours' , kitchen_arrival: false, department: 'Зал' },
  { position_id: 34, name: 'Су-шеф', section: 'Су-шеф', section_order: 70, breakfast: 'none' , kitchen_arrival: true, department: 'Кухня' },
  { position_id: 35, name: 'Грильщик', section: 'Гриль', section_order: 100, breakfast: 'unit' , kitchen_arrival: true, department: 'Кухня' },
  { position_id: 36, name: 'Маркетолог', section: null, section_order: 100, breakfast: 'none' , kitchen_arrival: false, department: null },
];

const staff: { employee_id: number; name: string; position_id: number; on_call?: boolean }[] = [
  { employee_id: 11, name: 'Султанов Михаил', position_id: 31 },
  { employee_id: 12, name: 'Андриан Ника', position_id: 32 },
  { employee_id: 13, name: 'Иванова Алина', position_id: 33 },
  { employee_id: 14, name: 'Лосев Ярослав', position_id: 33 },
  { employee_id: 15, name: 'Юлдашев Отабек', position_id: 34 },
  { employee_id: 16, name: 'Никешин Роман', position_id: 35 },
  { employee_id: 17, name: 'Балуева Мария', position_id: 36 },
];

/** Отметки: `дата|сотрудник` → приход, уход, должность смены. */
const sheetMarks = new Map<string, { started: string; finished: string | null; position_id: number | null }>([
  [`${day(0)}|11`, { started: '09:30', finished: null, position_id: null }],
  [`${day(0)}|13`, { started: '10:10', finished: '22:00', position_id: null }],
  [`${day(-1)}|13`, { started: '09:50', finished: '19:40', position_id: null }],
  [`${day(-1)}|15`, { started: '09:30', finished: '22:30', position_id: 35 }],
  [`${day(0)}|16`, { started: '10:40', finished: '23:00', position_id: null }],
]);

/** Приход кухни по правилу ресторана (D219) — копия правила сервера для заглушки. */
function ruledStart(started: string, applies: boolean): string {
  if (!applies || started < '05:00' || started > '11:10') return started;
  return started <= '09:45' ? '09:30' : '11:10';
}
let backfillOpen = false;

const toMinutes = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const tenth = (minutes: number) => (Math.round((minutes / 60) * 10) / 10).toFixed(1).replace('.', ',');

function sheetHours(started: string, finished: string | null, mode: BreakfastMode) {
  if (!finished) return { total: null, regular: null, breakfast: null };
  const from = toMinutes(started);
  let to = toMinutes(finished);
  if (to <= from) to += 24 * 60;
  const eaten = mode === 'none' ? 0 : Math.max(0, Math.min(to, 660) - Math.max(from, 570));
  return {
    total: tenth(to - from),
    regular: tenth(to - from - eaten),
    breakfast: !eaten ? null : mode === 'unit' ? '1,0' : tenth(eaten),
  };
}

function sheetFor(date: string): DaySheet {
  const placed = sheetPositions.filter((p) => p.section);
  const sections = new Map<
    string,
    { order: number; position_ids: number[]; department: string | null; entries: SheetEntry[] }
  >();
  placed.forEach((p) => {
    const bucket = sections.get(p.section!) ?? {
      order: p.section_order,
      position_ids: [],
      department: p.department,
      entries: [],
    };
    bucket.position_ids.push(p.position_id);
    sections.set(p.section!, bucket);
  });
  const free: DaySheet['free'] = [];
  for (const person of staff) {
    const markRow = sheetMarks.get(`${date}|${person.employee_id}`);
    if (!markRow) {
      free.push({
        employee_id: person.employee_id,
        name: person.name,
        position_id: person.position_id,
        last: person.employee_id === 14 ? { started: '11:50', finished: '23:20' } : null,
        on_call: Boolean(person.on_call),
      });
      continue;
    }
    const positionId = markRow.position_id ?? person.position_id;
    const position = placed.find((p) => p.position_id === positionId);
    if (!position) continue;
    const hours = sheetHours(ruledStart(markRow.started, position.kitchen_arrival), markRow.finished, position.breakfast);
    sections.get(position.section!)!.entries.push({
      employee_id: person.employee_id,
      name: person.name,
      position_id: positionId,
      position_name: position.name,
      home_position_id: person.position_id,
      on_call: Boolean(person.on_call),
      breakfast_mode: position.breakfast,
      started: markRow.started,
      finished: markRow.finished,
      open: !markRow.finished,
      total: hours.total,
      regular: hours.regular,
      breakfast: hours.breakfast,
      outside: null,
    });
  }
  const today = day(0);
  return {
    date,
    today,
    can_edit: date === today || (date < today && backfillOpen),
    is_keeper: true,
    can_see_table: true,
    geo_check: false,
    backfill_open: backfillOpen,
    can_switch_backfill: true,
    sections: [...sections.entries()]
      .sort((x, y) => x[1].order - y[1].order)
      .map(([title, value]) => ({
        title,
        position_ids: value.position_ids,
        department: value.department,
        department_position_ids: placed
          .filter((p) => p.department === value.department)
          .map((p) => p.position_id),
        entries: value.entries,
      })),
    free,
    positions: placed,
  };
}

function gridFor(from: string, to: string): HoursGrid {
  const days: string[] = [];
  for (let d = from; d <= to; d = new Date(Date.parse(`${d}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10)) {
    days.push(d);
  }
  const rows = new Map<string, GridRow & { section: string; order: number }>();
  const sum = (values: (string | null)[]) =>
    values
      .filter((v): v is string => Boolean(v))
      .reduce((acc, v) => acc + Number(v.replace(',', '.')), 0)
      .toFixed(1)
      .replace('.', ',');
  for (const person of staff) {
    for (const d of days) {
      const markRow = sheetMarks.get(`${d}|${person.employee_id}`);
      const positionId = markRow?.position_id ?? person.position_id;
      const position = sheetPositions.find((p) => p.position_id === positionId && p.section);
      if (!position || (!markRow && positionId !== person.position_id)) continue;
      const key = `${person.employee_id}|${positionId}`;
      if (!rows.has(key)) {
        rows.set(key, {
          employee_id: person.employee_id,
          name: person.name,
          position_id: positionId,
          position_name: position.name,
          breakfast_mode: position.breakfast,
          cells: {},
          regular_total: '0,0',
          breakfast_total: '0,0',
          fact_total: '0,0',
          open_count: 0,
          section: position.section!,
          order: position.section_order,
        });
      }
      if (markRow) {
        const started = ruledStart(markRow.started, position.kitchen_arrival);
        const hours = sheetHours(started, markRow.finished, position.breakfast);
        rows.get(key)!.cells[d] = {
          started,
          finished: markRow.finished,
          payroll_number: markRow.finished
            ? Number((started + markRow.finished).replace(/:/g, ''))
            : null,
          regular: hours.regular,
          breakfast: hours.breakfast,
          outside: false,
          ruled: started !== markRow.started,
          fact_started: markRow.started,
          fact_hours: sheetHours(markRow.started, markRow.finished, 'none').total,
        };
      }
    }
  }
  const sections = new Map<string, { order: number; rows: GridRow[] }>();
  for (const { section, order, ...row } of rows.values()) {
    const cells = Object.values(row.cells);
    row.regular_total = sum(cells.map((c) => c.regular));
    row.breakfast_total = sum(cells.map((c) => c.breakfast));
    row.fact_total = sum(cells.map((c) => c.fact_hours));
    row.open_count = cells.filter((c) => !c.finished).length;
    const bucket = sections.get(section) ?? { order, rows: [] };
    bucket.rows.push(row);
    sections.set(section, bucket);
  }
  const all = [...sections.entries()].sort((x, y) => x[1].order - y[1].order);
  return {
    from,
    to,
    today: day(0),
    days,
    sections: all.map(([title, value]) => ({ title, rows: value.rows })),
    regular_total: sum(all.flatMap(([, v]) => v.rows.map((r) => r.regular_total))),
    breakfast_total: sum(all.flatMap(([, v]) => v.rows.map((r) => r.breakfast_total))),
    kitchen_rule: { breakfast_from: '09:30', breakfast_until: '09:45', start: '11:10' },
  };
}

const positions = [
  { id: 1, name: 'Повар' },
  { id: 2, name: 'Шеф-повар' },
  { id: 3, name: 'Бармен' },
  { id: 4, name: 'Официант' },
];
const areas = [
  { id: 1, title: 'Кухня' },
  { id: 2, title: 'Бар' },
  { id: 3, title: 'Зал' },
];
const FIELD_TYPES = [
  { code: 'bool', title: 'Да / Нет' },
  { code: 'number', title: 'Число с диапазоном' },
  { code: 'text', title: 'Свободный текст' },
  { code: 'date', title: 'Дата' },
  { code: 'choice', title: 'Выбор из вариантов' },
  { code: 'photo', title: 'Только фотография' },
];

function item(order: number, title: string, extra: Partial<TemplateItem> = {}): TemplateItem {
  return {
    order_no: order,
    title,
    hint: null,
    field_type: 'bool',
    field_type_title: 'Да / Нет',
    photo_required: false,
    is_critical: false,
    num_min: null,
    num_max: null,
    num_unit: null,
    choice_options: null,
    assignee_position_id: null,
    assignee_position: null,
    allowed: null,
    ...extra,
  };
}

let nextTemplate = 30;
let nextSchedule = 80;
const templates: TemplateDetail[] = [
  build({
    id: 21,
    title: 'Открытие кухни',
    area: { id: 1, title: 'Кухня' },
    version: 3,
    published_version: 3,
    item_list: [
      item(1, 'Холодильная камера №1 закрыта'),
      item(2, 'Температура камеры №2', {
        field_type: 'number',
        field_type_title: 'Число с диапазоном',
        num_min: '-2',
        num_max: '4',
        num_unit: '°C',
        allowed: 'от -2 до 4 °C',
        is_critical: true,
        assignee_position_id: 2,
        assignee_position: 'Шеф-повар',
      }),
    ],
    schedule_list: [
      {
        id: 71,
        weekdays: [0, 1, 2, 3, 4, 5, 6],
        weekdays_text: 'ежедневно',
        window_from: '08:00',
        window_to: '10:00',
        spans_midnight: false,
        window_text: '08:00–10:00',
        positions: [{ id: 1, name: 'Повар' }],
      },
    ],
  }),
  build({ id: 22, title: 'Закрытие бара', area: { id: 2, title: 'Бар' }, version: 1, published_version: null, item_list: [], schedule_list: [] }),
];

function build(base: Pick<TemplateDetail, 'id' | 'title' | 'area' | 'version' | 'published_version' | 'item_list' | 'schedule_list'>): TemplateDetail {
  const status = base.published_version === null ? 'draft' : base.published_version === base.version ? 'published' : 'changed';
  const blockers = [
    ...(base.item_list.length ? [] : ['нет ни одного пункта']),
    ...(base.schedule_list.length ? [] : ['нет расписания']),
    ...(base.schedule_list.length && !base.schedule_list.some((s) => s.positions.length)
      ? ['не указано, каким должностям он адресован']
      : []),
  ];
  return {
    ...base,
    status,
    status_title:
      status === 'draft'
        ? 'черновик, никому не выдаётся'
        : status === 'changed'
          ? `смена работает по версии ${base.published_version}, правится версия ${base.version}`
          : `опубликован, выдаётся версия ${base.published_version}`,
    is_active: true,
    items: base.item_list.length,
    schedules: base.schedule_list.map((s) => `${s.weekdays_text}, ${s.window_text}`),
    updated_at: iso(-3000),
    description: null,
    can_edit: true,
    blockers,
    can_publish: blockers.length === 0 && base.published_version !== base.version,
    preview: base.item_list.map((entry, index) => ({
      position: index + 1,
      total: base.item_list.length,
      heading: `${base.title} — пункт ${index + 1} из ${base.item_list.length}`,
      title: entry.title,
      hint: entry.hint,
      allowed: entry.allowed,
      critical: entry.is_critical,
      field_type: entry.field_type,
      choice_options: entry.choice_options,
      photo_required: entry.photo_required || entry.field_type === 'photo',
    })),
  };
}

function save(template: TemplateDetail, changes: Partial<TemplateDetail>, content = false): TemplateDetail {
  let version = template.version;
  if (content && template.published_version === template.version) version += 1;
  const next = build({ ...template, ...changes, version });
  templates.splice(templates.indexOf(template), 1, next);
  return next;
}

function itemFrom(order: number, body: Record<string, unknown>): TemplateItem {
  const type = String(body.field_type ?? 'bool');
  const min = (body.num_min as string | null) ?? null;
  const max = (body.num_max as string | null) ?? null;
  const unit = (body.num_unit as string | null) ?? null;
  const positionId = (body.assignee_position_id as number | null) ?? null;
  return item(order, String(body.title), {
    field_type: type,
    field_type_title: FIELD_TYPES.find((t) => t.code === type)?.title ?? type,
    hint: (body.hint as string | null) ?? null,
    photo_required: Boolean(body.photo_required) || type === 'photo',
    is_critical: Boolean(body.is_critical),
    num_min: min,
    num_max: max,
    num_unit: unit,
    allowed: min && max ? `от ${min} до ${max}${unit ? ` ${unit}` : ''}` : null,
    choice_options: (body.choice_options as string[] | null) ?? null,
    assignee_position_id: positionId,
    assignee_position: positions.find((p) => p.id === positionId)?.name ?? null,
  });
}

const WEEKDAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const notFound = (text: string) => new ApiError(404, 'not found', text);
const conflict = (text: string) => new ApiError(409, 'conflict', text);
const bad = (text: string) => new ApiError(400, 'bad request', text);

/** «Вписать вручную» (D266): тёзка — вопрос, иначе новый вызывной. */
function addOnCall(body: Record<string, unknown>) {
  const name = String(body.full_name ?? '').trim().replace(/\s+/g, ' ');
  if (name.split(' ').length < 2) throw new ApiError(400, 'bad', 'Напишите имя и фамилию.');
  const words = (text: string) => text.toLowerCase().replace(/ё/g, 'е').split(' ').sort().join(' ');
  const twins = staff.filter((p) => words(p.name) === words(name));
  if (twins.length && !body.other_person) {
    return {
      status: 'same_name',
      matches: twins.map((p) => ({
        employee_id: p.employee_id,
        full_name: p.name,
        position_name: sheetPositions.find((x) => x.position_id === p.position_id)?.name ?? null,
      })),
    };
  }
  const employee_id = Math.max(...staff.map((p) => p.employee_id)) + 1;
  staff.push({ employee_id, name, position_id: Number(body.position_id), on_call: true });
  return { status: 'created', employee_id, full_name: name };
}

export async function mock(path: string, options: PortalRequest): Promise<unknown | undefined> {
  const [pathname, search = ''] = path.split('?');
  if (pathname === '/api/platform/timesheet/on-call') {
    return addOnCall((options.body ?? {}) as Record<string, unknown>);
  }
  if (!pathname.startsWith('/api/ops/shift/')) return undefined;
  const params = new URLSearchParams(search);
  const body = (options.body ?? {}) as Record<string, unknown>;
  const method = options.method ?? 'GET';

  if (pathname === '/api/ops/shift/access') {
    return {
      today: day(0),
      checklists: true,
      checklists_all: true,
      tasks: true,
      tasks_all: true,
      timesheet: true,
      timesheet_edit: true,
      timesheet_table: true,
      timesheet_setup: true,
      timesheet_setup_edit: true,
      discipline: true,
      templates: true,
      templates_edit: true,
      summary: true,
    };
  }
  if (pathname === '/api/ops/shift/checklists') return checklistDay(params.get('date'));
  const runMatch = /^\/api\/ops\/shift\/checklists\/(\d+)$/.exec(pathname);
  if (runMatch) return runDetail(Number(runMatch[1]));

  if (pathname === '/api/ops/shift/tasks') {
    return {
      whole: true,
      can_cancel: true,
      closed_days: 7,
      columns: {
        open: tasks.filter((t) => t.status === 'open'),
        in_progress: tasks.filter((t) => t.status === 'in_progress'),
        closed: tasks.filter((t) => !t.active),
      },
    };
  }
  const taskMatch = /^\/api\/ops\/shift\/tasks\/(\d+)(?:\/(take|resolve|cancel|photo))?$/.exec(pathname);
  if (taskMatch) {
    const row = tasks.find((t) => t.id === Number(taskMatch[1]));
    if (!row) throw notFound('Задача не найдена.');
    const action = taskMatch[2];
    if (!action) return taskCard(row);
    if (!row.active) throw conflict('Эта задача уже закрыта.');
    if (action === 'take') {
      if (row.assignee) throw conflict(`«${row.title}» уже взял ${row.assignee}.`);
      Object.assign(row, { assignee: 'Пётр Соколов', status: 'in_progress', status_title: STATUS_TITLES.in_progress });
      return { message: 'Взяли в работу.', card: taskCard(row) };
    }
    if (action === 'photo') {
      const photo = {
        photo_id: 950 + row.id,
        url: null,
        available: true,
        received_at: iso(0),
        forwarded: false,
        width: null,
        height: null,
      };
      photoByTask.set(row.id, photo);
      return { message: 'Снимок приложен.', photo };
    }
    if (action === 'resolve') {
      if (row.photo_required && body.photo_id == null)
        throw conflict('Эта задача закрывается с фотографией исправления — снимите или выберите файл и приложите его к закрытию.');
      if (!String(body.comment ?? '').trim()) throw bad('Напишите, что именно сделано.');
      Object.assign(row, { status: 'resolved', status_title: STATUS_TITLES.resolved, active: false, overdue: false, resolved_at: iso(0), resolved_by: 'Пётр Соколов', comment: String(body.comment) });
      return { message: `«${row.title}» закрыта. Спасибо.`, card: taskCard(row) };
    }
    if (!String(body.reason ?? '').trim()) throw bad('Напишите, почему задача отменяется.');
    Object.assign(row, { status: 'cancelled', status_title: STATUS_TITLES.cancelled, active: false, overdue: false, resolved_at: iso(0), comment: String(body.reason) });
    return { message: `«${row.title}» отменена.`, card: taskCard(row) };
  }

  if (pathname === '/api/ops/shift/timesheet/sheet') return sheetFor(params.get('date') ?? day(0));
  const sheetPath = /^\/api\/ops\/shift\/timesheet\/sheet\/(\d{4}-\d{2}-\d{2})\/(\d+)$/.exec(pathname);
  if (sheetPath) {
    const [, date, employee] = sheetPath;
    const sheet = sheetFor(date);
    if (!sheet.can_edit) throw conflict('Прошлые дни закрыты. Открыть их может управляющий или собственник.');
    const key = `${date}|${employee}`;
    if (method === 'DELETE') {
      sheetMarks.delete(key);
      return { message: 'Отметка убрана.' };
    }
    const clock = new Date().toTimeString().slice(0, 5);
    const started = body.started === 'now' ? clock : String(body.started);
    const finished = body.finished === 'now' ? clock : ((body.finished as string | null) || null);
    sheetMarks.set(key, { started, finished, position_id: (body.position_id as number | null) ?? null });
    const name = staff.find((p) => String(p.employee_id) === employee)?.name ?? 'сотрудник';
    return { message: `${name}: записано.`, shift: null };
  }
  if (pathname === '/api/ops/shift/timesheet/backfill') {
    backfillOpen = Boolean(body.open);
    return { backfill_open: backfillOpen, message: backfillOpen ? 'Прошлые дни открыты.' : 'Прошлые дни закрыты.' };
  }
  if (pathname === '/api/ops/shift/timesheet/grid') {
    const today = day(0);
    const from = params.get('from') ?? `${today.slice(0, 8)}${Number(today.slice(8)) <= 15 ? '01' : '16'}`;
    const to = params.get('to') ?? (from.endsWith('01') ? `${from.slice(0, 8)}15` : today);
    return gridFor(from, to);
  }
  const positionPath = /^\/api\/ops\/shift\/timesheet\/positions(?:\/(\d+))?$/.exec(pathname);
  if (positionPath) {
    if (positionPath[1]) {
      const row = sheetPositions.find((p) => p.position_id === Number(positionPath[1]));
      if (row) Object.assign(row, { section: (body.section as string | null) || null, section_order: Number(body.section_order), breakfast: body.breakfast, kitchen_arrival: Boolean(body.kitchen_arrival) });
    }
    const setup: PositionsSetup = {
      can_edit: true,
      breakfast_from: '09:30',
      breakfast_to: '11:00',
      kitchen_breakfast_until: '09:45',
      kitchen_start: '11:10',
      positions: sheetPositions,
    };
    return setup;
  }

  if (pathname === '/api/ops/shift/templates' && method === 'GET') {
    return {
      can_edit: true,
      templates: templates.map(({ item_list: _i, schedule_list: _s, preview: _p, blockers: _b, ...summary }) => summary),
      areas,
      positions,
      field_types: FIELD_TYPES,
    };
  }
  if (pathname === '/api/ops/shift/templates' && method === 'POST') {
    const title = String(body.title ?? '').trim();
    if (!title) throw bad('Название не может быть пустым.');
    const area = areas.find((a) => a.id === body.area_id) ?? null;
    const created = build({ id: nextTemplate++, title, area, version: 1, published_version: null, item_list: [], schedule_list: [] });
    templates.push(created);
    return created;
  }
  const tplMatch = /^\/api\/ops\/shift\/templates\/(\d+)(?:\/(items|schedules|publish)(?:\/(\d+)(?:\/(positions))?)?)?$/.exec(pathname);
  if (tplMatch) {
    const template = templates.find((t) => t.id === Number(tplMatch[1]));
    if (!template) throw notFound('Шаблон не найден.');
    const [, , part, sub, positionsPart] = tplMatch;
    if (!part) {
      if (method === 'PATCH') {
        const area = areas.find((a) => a.id === body.area_id);
        return save(template, { title: String(body.title ?? template.title), area: area ?? template.area });
      }
      return template;
    }
    if (part === 'items') {
      if (method === 'POST') {
        if (String(body.field_type) === 'choice' && ((body.choice_options as string[] | null) ?? []).length < 2) {
          throw bad('Нужно хотя бы два варианта ответа.');
        }
        const order = Math.max(0, ...template.item_list.map((i) => i.order_no)) + 1;
        return save(template, { item_list: [...template.item_list, itemFrom(order, body)] }, true);
      }
      const order = Number(sub);
      if (method === 'DELETE') {
        return save(template, { item_list: template.item_list.filter((i) => i.order_no !== order) }, true);
      }
      return save(template, { item_list: template.item_list.map((i) => (i.order_no === order ? itemFrom(order, body) : i)) }, true);
    }
    if (part === 'schedules') {
      if (method === 'POST') {
        const weekdays = (body.weekdays as number[]).slice().sort();
        const from = String(body.window_from);
        const to = String(body.window_to);
        const ids = body.position_ids as number[];
        const schedule = {
          id: nextSchedule++,
          weekdays,
          weekdays_text: weekdays.length === 7 ? 'ежедневно' : weekdays.map((d) => WEEKDAY_NAMES[d]).join(', '),
          window_from: from,
          window_to: to,
          spans_midnight: to <= from,
          window_text: to <= from ? `${from} → ${to}` : `${from}–${to}`,
          positions: positions.filter((p) => ids.includes(p.id)),
        };
        return save(template, { schedule_list: [...template.schedule_list, schedule] });
      }
      const scheduleId = Number(sub);
      if (method === 'DELETE') {
        return save(template, { schedule_list: template.schedule_list.filter((s) => s.id !== scheduleId) });
      }
      if (positionsPart) {
        const ids = body.position_ids as number[];
        return save(template, {
          schedule_list: template.schedule_list.map((s) =>
            s.id === scheduleId ? { ...s, positions: positions.filter((p) => ids.includes(p.id)) } : s,
          ),
        });
      }
    }
    if (part === 'publish') {
      if (template.blockers.length) throw bad(`Опубликовать нельзя: ${template.blockers.join(', ')}.`);
      if (template.published_version === template.version) throw conflict(`Версия ${template.version} уже опубликована.`);
      const published = save(template, { published_version: template.version });
      return {
        message: `«${published.title}» опубликован, версия ${published.version}. Прохождения начнут создаваться со следующего операционного дня.`,
        template: published,
      };
    }
  }

  if (pathname === '/api/ops/shift/discipline') {
    const totals = (planned: number, onTime: number, late: number, missed: number) => ({
      planned,
      done_on_time: onTime,
      done_late: late,
      missed,
      items_total: planned * 6,
      items_problem: Math.round(planned / 4),
      score: planned ? Math.round((onTime * 100) / planned) : null,
    });
    return {
      today: day(0),
      period: { from: params.get('from') ?? day(-7), to: params.get('to') ?? day(-1) },
      manager: true,
      target: 80,
      my_runs: runs.slice(0, 2),
      tasks: tasks.filter((t) => t.active),
      day: { date: day(0), ...totals(5, 1, 1, 1), in_work: 2, runs },
      not_done: runs.filter((r) => ['pending', 'in_progress', 'missed'].includes(r.status)),
      score: totals(35, 29, 3, 3),
      employees: [
        { employee_id: 13, name: 'Олег Смирнов', done: 6, ...totals(7, 4, 2, 1) },
        { employee_id: 12, name: 'Иван Петров', done: 9, ...totals(9, 8, 1, 0) },
        { employee_id: 11, name: 'Анна Морозова', done: 10, ...totals(10, 10, 0, 0) },
      ],
    };
  }
  return undefined;
}
